/**
 * Iter 144+ — Mobile Refuelling Log — Fixed & Offline-capable.
 *
 * Changes from previous version:
 * - Tank is truly OPTIONAL for all kinds including tank_refill
 * - Photos are OPTIONAL (strong encouragement but never block submit)
 * - GPS is captured at the moment each photo is taken (not on page load)
 * - Offline-first: if network fails, logs saved to localStorage and synced when online
 * - No liveness check on photo capture (just camera capture — fast at petrol pump)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, FilePlus2, Send, Loader2, MapPin, Camera, Check, X, WifiOff, RefreshCw,
} from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { enqueue, queueCount, syncQueue } from '../../lib/offlineQueue';

// Local-time datetime string (not UTC) for datetime-local input
function localDatetimeNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Quick non-blocking GPS grab (5s timeout, fail open)
async function quickGPS() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const t = setTimeout(() => resolve(null), 5000);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(t); resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); },
      () => { clearTimeout(t); resolve(null); },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 },
    );
  });
}

// localDatetimeNow and quickGPS helpers below

const FUEL_KINDS = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'tank_refill', label: 'Tank Refill' },
];

const PAYMENT_MODES = ['Fuel Card', 'Cash', 'UPI', 'Credit Account'];

// Photo steps — all OPTIONAL (encouragement only, never block submit)
const PHOTO_STEPS = [
  { slot: 'meter_zero', label: 'Pump meter at ZERO',
    hint: 'Pump display showing 0 BEFORE filling.' },
  { slot: 'vehicle', label: 'Vehicle being fuelled',
    hint: 'Whole vehicle including number plate.' },
  { slot: 'meter_final', label: 'Final meter reading',
    hint: 'Final pump display with amount.' },
  { slot: 'pump_bill', label: 'Pump bill / receipt',
    hint: 'Lay bill flat, capture entire receipt.' },
];

// Compress + base64-encode a captured image client-side so the JSON payload
// stays manageable. Max edge 1600 px / JPEG q ≈ 0.75 mirrors the server-side
// retention envelope, so the round-trip stays cheap.
async function fileToCompressedBase64(file, maxDim = 1600, quality = 0.75) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  // strip the "data:image/jpeg;base64," prefix — backend tolerates both
  return dataUrl.split(',', 2)[1];
}

export default function MobileFuelLog() {
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [drivers, setDrivers] = useState([]);          // driver pool
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDrop, setShowDriverDrop] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState({});
  const [offlineCount, setOfflineCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [gpsCaptured, setGpsCaptured] = useState(false);
  const fileRefs = useRef({ meter_zero: null, vehicle: null, meter_final: null, pump_bill: null, driver_selfie: null });
  const [form, setForm] = useState({
    kind: 'vehicle',
    vehicle_id: '',
    equipment_id: '',
    tank_id: '',       // required for tank_refill, optional otherwise
    driver_name: '',
    log_datetime: localDatetimeNow(),
    fuel_station: '',
    fuel_type: '',
    fuel_quantity_l: 0,
    rate_per_l: 0,
    total_amount: 0,
    odometer_reading: '',
    hour_meter_reading: '',
    payment_mode: 'Fuel Card',
    gps_lat: null,
    gps_lng: null,
    remarks: '',
  });

  useEffect(() => {
    apiGet('/fuel/vehicles').then((d) => setVehicles(d?.items || [])).catch(() => {});
    apiGet('/fuel/equipment').then((d) => setEquipment(d?.items || [])).catch(() => {});
    apiGet('/fuel/tanks').then((d) => setTanks(d?.items || [])).catch(() => {});
    apiGet('/fuel/drivers', { names: 'true' }).then((d) => setDrivers(d?.drivers || [])).catch(() => {});
    queueCount().then(setOfflineCount).catch(() => {});
  }, []);

  const filteredDrivers = driverSearch
    ? drivers.filter(d => d.toLowerCase().includes(driverSearch.toLowerCase()))
    : drivers;

  const set = (k, v) => setForm((f) => {
    const nf = { ...f, [k]: v };
    if (k === 'fuel_quantity_l' || k === 'rate_per_l') {
      const q = Number(k === 'fuel_quantity_l' ? v : nf.fuel_quantity_l);
      const r = Number(k === 'rate_per_l' ? v : nf.rate_per_l);
      if (q && r) nf.total_amount = Number((q * r).toFixed(2));
    }
    // Auto-fill fuel_type when vehicle or equipment is selected
    if (k === 'vehicle_id') {
      const veh = vehicles.find(vv => vv.id === v);
      if (veh?.fuel_type) nf.fuel_type = veh.fuel_type;
    }
    if (k === 'equipment_id') {
      const eq = equipment.find(e => e.id === v);
      if (eq?.fuel_type) nf.fuel_type = eq.fuel_type;
    }
    return nf;
  });

  const capturedCount = PHOTO_STEPS.reduce((n, s) => n + (photos[s.slot] ? 1 : 0), 0);
  const photosRequired = form.kind === 'vehicle' || form.kind === 'equipment';
  const allPhotosCaptured = !photosRequired || capturedCount === PHOTO_STEPS.length;

  const onPick = async (slot, file) => {
    if (!file) return;
    // GPS: capture once on the very first photo — not on every photo.
    // The first photo is taken at the pump, which is the location we want.
    // Subsequent photos are taken at the same location so re-capturing wastes time.
    let gpsForPhoto = null;
    if (!gpsCaptured && !form.gps_lat) {
      gpsForPhoto = await quickGPS();
      if (gpsForPhoto) {
        setGpsCaptured(true);
        setForm(f => ({ ...f, gps_lat: gpsForPhoto.lat, gps_lng: gpsForPhoto.lng }));
      }
    }
    try {
      const base64 = await fileToCompressedBase64(file);
      if (!base64) { toast.error('Could not read photo — try again'); return; }
      const preview = URL.createObjectURL(file);
      setPhotos((p) => {
        if (p[slot]?.preview) URL.revokeObjectURL(p[slot].preview);
        return { ...p, [slot]: { preview, base64 } };
      });
    } catch {
      toast.error('Could not read photo — try again');
    }
  };

  const clearPhoto = (slot) => setPhotos((p) => {
    if (p[slot]?.preview) URL.revokeObjectURL(p[slot].preview);
    const nx = { ...p }; delete nx[slot]; return nx;
  });

  // Offline sync via lib/offlineQueue.js (IndexedDB — handles large base64 photos safely)
  const syncOfflineQueue = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await syncQueue();
      const remaining = await queueCount();
      setOfflineCount(remaining);
      if (result.synced > 0) toast.success(`${result.synced} offline fuel log(s) synced`);
      if (remaining > 0) toast.error(`${remaining} log(s) still pending — check connection`);
    } catch {
      toast.error('Sync failed — check connection');
    } finally { setSyncing(false); }
  }, []);

  const buildPayload = () => {
    const p = {
      ...form,
      log_datetime: new Date(form.log_datetime).toISOString(),
      fuel_quantity_l: Number(form.fuel_quantity_l || 0),
      rate_per_l: Number(form.rate_per_l || 0),
      total_amount: Number(form.total_amount || 0),
      odometer_reading: form.odometer_reading ? Number(form.odometer_reading) : null,
      hour_meter_reading: form.hour_meter_reading ? Number(form.hour_meter_reading) : null,
      vehicle_id: form.kind === 'vehicle' ? form.vehicle_id || null : null,
      equipment_id: form.kind === 'equipment' ? form.equipment_id || null : null,
      tank_id: form.tank_id || null,   // required for tank_refill, optional for vehicle/equipment
    };
    // Photos are OPTIONAL — include whatever was captured
    PHOTO_STEPS.forEach(({ slot }) => {
      if (photos[slot]?.base64) {
        p[`${slot}_photo_base64`] = photos[slot].base64;
        p[`${slot}_photo_mime`] = 'image/jpeg';
      }
    });
    if (photos.driver_selfie?.base64) {
      p.driver_selfie_base64 = photos.driver_selfie.base64;
      p.driver_selfie_mime = 'image/jpeg';
    }
    return p;
  };

  const submit = async (rateConfirmed = false) => {
    if (form.kind === 'vehicle' && !form.vehicle_id) return toast.error('Pick a vehicle');
    if (form.kind === 'equipment' && !form.equipment_id) return toast.error('Pick an equipment');
    if (form.kind === 'tank_refill' && !form.tank_id) return toast.error('Select the tank being refilled');
    if (!form.fuel_quantity_l || !form.rate_per_l) return toast.error('Quantity & rate required');
    if (photosRequired && !allPhotosCaptured) {
      const missing = PHOTO_STEPS.filter(s => !photos[s.slot]).map(s => s.label).join(', ');
      return toast.error(`Capture all 4 photos first. Missing: ${missing}`);
    }
    setSubmitting(true);
    const payload = { ...buildPayload(), rate_confirmed: rateConfirmed };
    try {
      const res = await apiPost('/fuel/logs', payload);
      if (res?.rate_warning) {
        setSubmitting(false);
        if (window.confirm(res.rate_warning)) return submit(true);
        return;
      }
      toast.success('Fuel expense submitted.');
      nav('/m/fuel');
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Check if it's a network/offline error (no response)
      if (!e?.response) {
        // Network failure — save to IndexedDB (offlineQueue handles large base64 safely)
        try {
          await enqueue('fuel', {
            label: `Fuel Log — ${form.kind} · ${form.fuel_quantity_l}L`,
            payload,
          });
          const count = await queueCount();
          setOfflineCount(count);
          setSubmitting(false);
          toast.success(`Saved offline — will sync when connected (${count} pending)`);
          nav('/m/fuel');
        } catch (qErr) {
          // enqueue itself failed (very unusual) — tell user explicitly
          toast.error('Could not save offline. Please retry when connected.');
          setSubmitting(false);
        }
        return;
      }
      if (d?.validation) {
        const failed = d.validation.filter(c => c.status === 'fail');
        const msg = failed.length
          ? failed.map(c => `${c.name}: ${c.detail}`).join('\n')
          : (d.message || 'Validation failed');
        toast.error(msg, { duration: 8000 });
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message || 'Submit failed'));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-40" data-testid="m-fuel-log">
      <div className="bg-[#1F7A6E] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m/fuel" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold flex items-center gap-1.5"><FilePlus2 size={14} /> Refuelling Log</div>
          <div className="text-[10px] text-slate-300">Capture a fuel entry</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {form.gps_lat && <span className="text-[10px] flex items-center gap-1 text-emerald-300"><MapPin size={11} /> GPS</span>}
          {offlineCount > 0 && (
            <button onClick={syncOfflineQueue} disabled={syncing}
              className="text-[10px] flex items-center gap-1 text-amber-300 bg-amber-400/20 px-2 py-0.5 rounded"
              data-testid="fuel-sync-offline-btn">
              {syncing ? <RefreshCw size={10} className="animate-spin" /> : <WifiOff size={10} />}
              {offlineCount} pending
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <Section label="Kind">
          <div className="grid grid-cols-3 gap-1.5">
            {FUEL_KINDS.map((k) => (
              <button
                key={k.value}
                onClick={() => set('kind', k.value)}
                data-testid={`m-fuel-kind-${k.value}`}
                className={`px-2 py-2 rounded-sm text-xs font-display font-bold border ${
                  form.kind === k.value ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-700 border-slate-300'
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </Section>

        {form.kind === 'vehicle' && (
          <Section label="Vehicle">
            <select value={form.vehicle_id} onChange={(e) => set('vehicle_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-fuel-vehicle">
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {`${v.vehicle_number} · ${v.vehicle_type}`}
                  {(v.project_name || v.office_location_name) ? ` (${v.project_name || v.office_location_name})` : ''}
                </option>
              ))}
            </select>
          </Section>
        )}

        {form.kind === 'equipment' && (
          <Section label="Equipment">
            <select value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-fuel-equipment">
              <option value="">Select equipment…</option>
              {equipment.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {`${eq.equipment_id} · ${eq.equipment_type}`}
                  {(eq.project_name || eq.office_location_name) ? ` (${eq.project_name || eq.office_location_name})` : ''}
                </option>
              ))}
            </select>
          </Section>
        )}

        {/* Tank — REQUIRED for tank_refill, hidden for vehicle/equipment */}
        {form.kind === 'tank_refill' && (
          <Section label="Tank Being Refilled *">
            <select value={form.tank_id} onChange={(e) => set('tank_id', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-fuel-tank">
              <option value="">— Select tank —</option>
              {tanks.map((t) => <option key={t.id} value={t.id}>{`${t.tank_id} · ${Math.round(t.current_level_l ?? 0)}L of ${Math.round(t.capacity_l ?? 0)}L`}</option>)}
            </select>
          </Section>
        )}

        <Section label="Date &amp; Time">
          <input type="datetime-local" value={form.log_datetime} onChange={(e) => set('log_datetime', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
        </Section>

        {/* Fuel type is auto-read from the selected vehicle/equipment — not editable */}
        {form.fuel_type && (
          <Section label="Fuel Type (from vehicle)">
            <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-sm text-sm text-slate-700 font-medium" data-testid="m-fuel-type-display">
              {form.fuel_type}
            </div>
          </Section>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Section label="Qty (L)">
            <input type="number" inputMode="decimal" value={form.fuel_quantity_l} onChange={(e) => set('fuel_quantity_l', e.target.value)} className="w-full px-2 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="m-fuel-qty" />
          </Section>
          <Section label="Rate ₹/L">
            <input type="number" inputMode="decimal" value={form.rate_per_l} onChange={(e) => set('rate_per_l', e.target.value)} className="w-full px-2 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="m-fuel-rate" />
          </Section>
          <Section label="Total ₹">
            <input type="number" inputMode="decimal" value={form.total_amount} onChange={(e) => set('total_amount', e.target.value)} className="w-full px-2 py-2.5 border border-slate-300 rounded-sm text-sm font-display font-bold" data-testid="m-fuel-total" />
          </Section>
        </div>

        {form.kind === 'vehicle' && (
          <Section label="Odometer (km)">
            <input type="number" inputMode="decimal" value={form.odometer_reading} onChange={(e) => set('odometer_reading', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
          </Section>
        )}

        {form.kind === 'equipment' && (
          <Section label="Hour Meter">
            <input type="number" inputMode="decimal" value={form.hour_meter_reading} onChange={(e) => set('hour_meter_reading', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
          </Section>
        )}

        <Section label="Driver (who is filling fuel now)">
          <div className="relative">
            <input
              value={driverSearch || form.driver_name}
              onChange={(e) => { setDriverSearch(e.target.value); set('driver_name', e.target.value); setShowDriverDrop(true); }}
              onFocus={() => setShowDriverDrop(true)}
              placeholder="Search or type driver name…"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
              data-testid="m-fuel-driver"
            />
            {showDriverDrop && filteredDrivers.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-y-auto mt-0.5">
                {filteredDrivers.map(d => (
                  <button key={d} type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50"
                    onMouseDown={() => { set('driver_name', d); setDriverSearch(''); setShowDriverDrop(false); }}
                    data-testid={`m-fuel-driver-opt-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Section>

        <Section label="Fuel Station">
          <input value={form.fuel_station} onChange={(e) => set('fuel_station', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
        </Section>

        <Section label="Payment Mode">
          <select value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
            {PAYMENT_MODES.map((p) => <option key={p}>{p}</option>)}
          </select>
        </Section>

        {/* Photos section — OPTIONAL, encouraged but never blocking */}
        {(form.kind === 'vehicle' || form.kind === 'equipment') && (
          <Section label={photosRequired ? `Photos ${capturedCount}/4 (required for vehicle/equipment)` : `Photos ${capturedCount}/4 (optional for tank refill)`}>
            <div className="space-y-2" data-testid="m-fuel-photo-steps">
              {PHOTO_STEPS.map((step, idx) => {
                const captured = photos[step.slot];
                return (
                  <div key={step.slot} className={`bg-white rounded-sm border p-2 ${captured ? 'border-emerald-400' : 'border-slate-300'}`}>
                    <div className="flex items-start gap-2">
                      <div className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${captured ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                        {captured ? <Check size={14} /> : idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-display font-bold">{`Photo ${idx + 1} of 4 — ${step.label}`}</div>
                        <div className="text-[10px] text-slate-500 leading-snug mt-0.5">{step.hint}</div>
                      </div>
                    </div>
                    <input
                      ref={(el) => { fileRefs.current[step.slot] = el; }}
                      hidden
                      type="file"
                      accept="image/*"
                      capture="environment"
                      data-testid={`m-fuel-photo-input-${step.slot}`}
                      onChange={(e) => onPick(step.slot, e.target.files?.[0])}
                    />
                    {captured ? (
                      <div className="mt-2 relative">
                        <img src={captured.preview} alt={step.label} className="w-full h-40 object-cover rounded-sm" />
                        <button
                          onClick={() => clearPhoto(step.slot)}
                          className="absolute top-1.5 right-1.5 bg-white/95 text-slate-800 rounded-full p-1 shadow"
                          data-testid={`m-fuel-photo-retake-${step.slot}`}
                          aria-label={`Retake ${step.label}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileRefs.current[step.slot]?.click()}
                        className="mt-2 w-full bg-slate-50 border-2 border-dashed border-slate-300 rounded-sm p-3 text-center"
                        data-testid={`m-fuel-photo-capture-${step.slot}`}
                      >
                        <Camera size={18} className="mx-auto text-slate-400 mb-1" />
                        <div className="text-[11px] font-display font-bold">Tap to open camera</div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        <Section label="Remarks">
          <textarea rows={2} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
        </Section>

        {/* FIX 8 — optional 5th photo: driver selfie */}
        <Section label="Driver Selfie (optional)">
          <input
            ref={(el) => { fileRefs.current.driver_selfie = el; }}
            hidden type="file" accept="image/*" capture="user"
            data-testid="m-fuel-photo-input-driver_selfie"
            onChange={(e) => onPick('driver_selfie', e.target.files?.[0])}
          />
          {photos.driver_selfie ? (
            <div className="relative">
              <img src={photos.driver_selfie.preview} alt="Driver selfie" className="w-full h-40 object-cover rounded-sm" />
              <button onClick={() => clearPhoto('driver_selfie')} className="absolute top-1.5 right-1.5 bg-white/95 text-slate-800 rounded-full p-1 shadow" data-testid="m-fuel-photo-retake-driver_selfie">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button onClick={() => fileRefs.current.driver_selfie?.click()}
              className="w-full bg-white border-2 border-dashed border-slate-300 rounded-sm p-3 text-center"
              data-testid="m-fuel-photo-capture-driver_selfie">
              <Camera size={18} className="mx-auto text-slate-400 mb-1" />
              <div className="text-[11px] font-display font-bold">Add driver selfie (optional)</div>
            </button>
          )}
        </Section>
      </div>

      {/* Offline banner */}
      {offlineCount > 0 && (
        <div className="fixed inset-x-0 bg-amber-500/90 text-white text-xs px-4 py-2 z-40 flex items-center justify-between" style={{ bottom: 'calc(8rem + env(safe-area-inset-bottom))' }} data-testid="fuel-offline-banner">
          <span className="flex items-center gap-1.5"><WifiOff size={12} /> {offlineCount} fuel log(s) saved offline</span>
          <button onClick={syncOfflineQueue} className="font-bold underline" disabled={syncing}>{syncing ? 'Syncing…' : 'Sync now'}</button>
        </div>
      )}

      {/* Sticky submit */}
      <div
        className="fixed inset-x-0 bg-white border-t border-slate-200 px-4 py-3 z-50"
        style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
      >
        {capturedCount > 0 && capturedCount < PHOTO_STEPS.length && (
          <p className="text-[10px] text-slate-400 text-center mb-1.5">
            {capturedCount}/{PHOTO_STEPS.length} photos captured — you can submit without the rest
          </p>
        )}
        <button
          onClick={() => submit()}
          disabled={submitting || (photosRequired && !allPhotosCaptured)}
          className="w-full bg-[#1F7A6E] hover:bg-[#14504E] disabled:opacity-50 text-white font-display font-bold rounded-sm py-3 flex items-center justify-center gap-2"
          data-testid="m-fuel-submit"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {submitting
            ? 'Submitting…'
            : (photosRequired && !allPhotosCaptured)
              ? `Capture all 4 photos (${capturedCount}/4)`
              : 'Submit Fuel Expense'}
        </button>
      </div>
    </div>
  );
}

const Section = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500 px-1">{label}</label>
    {children}
  </div>
);
